import { useState, useEffect } from 'react';

/** The rebuild's single phone/desktop split: Tailwind `lg` (see Header/index.js). */
export const DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * A phone turned sideways: landscape, short (under 500px tall) and below the desktop width.
 * Keyed on height so tablets and narrow desktop windows are not caught. Same query as the
 * Tailwind `lp:` variant in tailwind.config.js.
 */
export const LANDSCAPE_PHONE_QUERY = '(orientation: landscape) and (max-height: 499px) and (max-width: 1023px)';

/** True while `query` matches; follows resizes and rotation. */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = e => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/**
 * True on a phone held sideways (LANDSCAPE_PHONE_QUERY). The fretted boards switch to a wide,
 * short 15-fret layout so the whole neck fits on screen.
 */
export function useIsLandscapePhone() {
  return useMediaQuery(LANDSCAPE_PHONE_QUERY);
}

/**
 * Returns true at the desktop layout width (1024px and up), false for the phone layout.
 * Use this when a component needs the split in JS (e.g. a `size` prop or fret count);
 * prefer `lg:` classes when CSS alone can do it.
 */
export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const handler = e => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isDesktop;
}
